import React, { useEffect, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@heroui/react';
import { ResponsiveTooltip, Typography } from '../../../../components';
import { RevshareOffer } from '../../../../api/client/clientRevshareService';
import { useTranslations } from '../../../../contexts/translation.context';
import BlockedCountriesTooltip from '../../../../partner/subscribers/orders/components/BlockedCountriesTooltip';
import ProhibitedSourcesTooltip from './ProhibitedSourcesTooltip';

interface RevshareDetailsCellProps {
  offer: RevshareOffer;
}

const RevshareDetailsCell: React.FC<RevshareDetailsCellProps> = ({ offer }) => {
  const { t } = useTranslations();

  const [isBlockedCountriesOpen, setIsBlockedCountriesOpen] = useState(false);
  const [isProhibitedSourcesOpen, setIsProhibitedSourcesOpen] = useState(false);
  const blockedCountriesTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prohibitedSourcesTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (blockedCountriesTimeoutRef.current) {
        clearTimeout(blockedCountriesTimeoutRef.current);
      }
      if (prohibitedSourcesTimeoutRef.current) {
        clearTimeout(prohibitedSourcesTimeoutRef.current);
      }
    };
  }, []);

  // Blocked countries handlers
  const openBlockedCountries = () => {
    if (blockedCountriesTimeoutRef.current) {
      clearTimeout(blockedCountriesTimeoutRef.current);
      blockedCountriesTimeoutRef.current = null;
    }
    setIsBlockedCountriesOpen(true);
  };

  const scheduleBlockedCountriesClose = () => {
    if (blockedCountriesTimeoutRef.current) {
      clearTimeout(blockedCountriesTimeoutRef.current);
    }
    blockedCountriesTimeoutRef.current = setTimeout(() => {
      setIsBlockedCountriesOpen(false);
    }, 120);
  };

  // Prohibited sources handlers
  const openProhibitedSources = () => {
    if (prohibitedSourcesTimeoutRef.current) {
      clearTimeout(prohibitedSourcesTimeoutRef.current);
      prohibitedSourcesTimeoutRef.current = null;
    }
    setIsProhibitedSourcesOpen(true);
  };

  const scheduleProhibitedSourcesClose = () => {
    if (prohibitedSourcesTimeoutRef.current) {
      clearTimeout(prohibitedSourcesTimeoutRef.current);
    }
    prohibitedSourcesTimeoutRef.current = setTimeout(() => {
      setIsProhibitedSourcesOpen(false);
    }, 120);
  };

  const commission = offer.offer?.commission ?? 0;
  const prohibitedSourcesList = offer.offer?.prohibited_sources || [];
  const blockedCountriesList = offer.of_data?.blocked_countries || [];
  const offerShortId = offer?._id ? offer._id.slice(-4).toUpperCase() : '----';

  const byLinkText = t('revshare.table.byLink');
  const commissionText = t('revshare.table.commission');
  const blockedResourcesText = t('revshare.table.blockedResources');
  const blockedCountriesText = t('revshare.table.blockedCountries');

  return (
    <div className="flex flex-col gap-1">
      <Typography variant="body3" className="text-auth-ink font-semibold text-xs leading-4">
        {typeof byLinkText === 'string' ? byLinkText : 'По ссылке'}
      </Typography>

      <ResponsiveTooltip content={t('revshare.table.commissionTooltip')} placement="top">
        <div className="flex items-center gap-1">
          <Typography variant="body3" className="text-zinc-500 text-xs leading-4">
            {typeof commissionText === 'string' ? commissionText : 'Комиссия'}:
          </Typography>
          <Typography variant="body3" className="text-auth-ink font-semibold text-sm leading-4">
            {commission}%
          </Typography>
        </div>
      </ResponsiveTooltip>

      {/* Заблокированные ресурсы (prohibited sources) */}
      {prohibitedSourcesList.length > 0 ? (
        <Popover
          placement="bottom-start"
          offset={12}
          showArrow
          isOpen={isProhibitedSourcesOpen}
          onOpenChange={setIsProhibitedSourcesOpen}
          shouldCloseOnBlur
          isDismissable
        >
          <PopoverTrigger>
            <div
              className="flex items-center gap-1 cursor-pointer"
              onMouseEnter={openProhibitedSources}
              onMouseLeave={scheduleProhibitedSourcesClose}
            >
              <Typography variant="body3" className="text-zinc-500 text-xs leading-4">
                {typeof blockedResourcesText === 'string'
                  ? blockedResourcesText
                  : 'Заблокированные ресурсы'}
                :
              </Typography>
              <Typography variant="body3" className="text-auth-ink font-semibold text-sm leading-4">
                {prohibitedSourcesList.length}
              </Typography>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 bg-white border border-tin rounded-2xl shadow-xl"
            onMouseEnter={openProhibitedSources}
            onMouseLeave={scheduleProhibitedSourcesClose}
          >
            <ProhibitedSourcesTooltip
              sources={prohibitedSourcesList}
              offerShortId={offerShortId}
              modelUsername={offer.of_user_name}
              profileUrl={`https://onlyfans.com/${offer.of_user_name}`}
            />
          </PopoverContent>
        </Popover>
      ) : (
        <div className="flex items-center gap-1">
          <Typography variant="body3" className="text-zinc-500 text-xs leading-4">
            {typeof blockedResourcesText === 'string'
              ? blockedResourcesText
              : 'Заблокированные ресурсы'}
            :
          </Typography>
          <Typography variant="body3" className="text-auth-ink font-semibold text-sm leading-4">
            0
          </Typography>
        </div>
      )}

      {/* Заблокированные страны */}
      {blockedCountriesList.length > 0 ? (
        <Popover
          placement="bottom-start"
          offset={12}
          showArrow
          isOpen={isBlockedCountriesOpen}
          onOpenChange={setIsBlockedCountriesOpen}
          shouldCloseOnBlur
          isDismissable
        >
          <PopoverTrigger>
            <div
              className="flex items-center gap-1 cursor-pointer"
              onMouseEnter={openBlockedCountries}
              onMouseLeave={scheduleBlockedCountriesClose}
            >
              <Typography variant="body3" className="text-zinc-500 text-xs leading-4">
                {typeof blockedCountriesText === 'string'
                  ? blockedCountriesText
                  : 'Заблокированные страны'}
                :
              </Typography>
              <Typography variant="body3" className="text-auth-ink font-semibold text-sm leading-4">
                {blockedCountriesList.length}
              </Typography>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 bg-white border border-tin rounded-2xl shadow-xl"
            onMouseEnter={openBlockedCountries}
            onMouseLeave={scheduleBlockedCountriesClose}
          >
            <BlockedCountriesTooltip
              countries={blockedCountriesList}
              offerShortId={offerShortId}
              modelUsername={offer.of_user_name}
              profileUrl={`https://onlyfans.com/${offer.of_user_name}`}
            />
          </PopoverContent>
        </Popover>
      ) : (
        <div className="flex items-center gap-1">
          <Typography variant="body3" className="text-zinc-500 text-xs leading-4">
            {typeof blockedCountriesText === 'string'
              ? blockedCountriesText
              : 'Заблокированные страны'}
            :
          </Typography>
          <Typography variant="body3" className="text-auth-ink font-semibold text-sm leading-4">
            0
          </Typography>
        </div>
      )}
    </div>
  );
};

export default RevshareDetailsCell;
